import React from 'react';
import { IndexRoute, Redirect, Route } from 'react-router';

import authService from '../state/services/auth';

import App from '../components/App';
import Layout from '../components/Layout';
import Page from '../components/Page/Page.react';

import {
  LoadableHealthcheck,
  LoadableLogin,
  LoadableLoginCallback,
  LoadableLogout,
  LoadableRaceAlerts,
  LoadableRaceAlertsAdd,
  LoadableStripeConnectLanding,
  LoadableStripeConnectSuccess,
  LoadableSavedEventsPage,
  LoadableLikeActivityPage,
  LoadableUnlikeActivityPage,
  LoadableCommentOnActivityPage,
  LoadableSaveEventPage,
  LoadableUnsaveEventPage,
  LoadableSaveExternalBookingPage,
  LoadableUnsaveExternalBookingPage,
  LoadableSaveSearchPage,
  LoadableUnsaveSearchPage,
  LoadableEventSearchPage,
  LoadablePublicProfilePage,
  LoadablePrivateProfilePage,
  LoadableProfileSettingsPage,
  LoadableBookingFormPage,
  LoadablePrivacy,
  LoadableTerms,
  LoadableNotFound,
} from './loadables';

import Homepage from '../containers/Homepage';
import AboutUsPage from '../components/AboutUsPage';
import DiscoveryPage from '../components/Discovery';
import EventDetailPage from '../containers/EventDetailPage';
import EventReviewPage from '../containers/EventReviewPage';
import EventTicketsPage from '../components/EventTicketsPage';
import OrganiserDetailPage from '../containers/OrganiserDetailPage';

import { redirectDiscoveryWithLocationWithoutLocale } from './helpers';

// Route configuration
// Read more https://github.com/rackt/react-router/blob/latest/docs/Glossary.md#routeconfig
const routes = (
  <Route path="/" component={App}>
    { /* Healthcheck */ }
    <Route path="/healthcheck" component={LoadableHealthcheck} />

    { /* Auth */ }
    <Route path="/login" component={LoadableLogin} />
    <Route path="/login/callback" component={LoadableLoginCallback} />
    <Route path="/logout" component={LoadableLogout} />

    { /* Auth variations */ }
    <Redirect from="/find" to="/login" />

    {/* Race Alerts - this does not have any internal inbound links */}
    <Route path="/race-alerts/add" component={LoadableRaceAlertsAdd} onEnter={authService.authenticateRoute} whiteBackground />

    { /* Stripe Connect Accounts */ }
    <Route path="/stripe/connect-account" component={LoadableStripeConnectLanding} />
    <Route path="/stripe/connect-success" component={LoadableStripeConnectSuccess} />

    {/* Deprecated calendar discovery pages */}
    <Route path="/:localeSlug/:sportSlug-events/calendar(/:dateSlug)" component={LoadableNotFound} status={410} />
    <Route path="/:localeSlug/:sportSlug-events(/:filterSlug)/calendar(/:dateSlug)" component={LoadableNotFound} status={410} />
    <Route path="/:sportSlug-events(/in-:locationSlug)/calendar(/:dateSlug)" component={LoadableNotFound} status={410} />
    <Route path="/:sportSlug-events(/:filterSlug)(/in-:locationSlug)/calendar(/:dateSlug)" component={LoadableNotFound} status={410} />

    <Route component={Layout}>
      { /* Homepage route */ }
      <IndexRoute component={Homepage} transparentHeader whiteBackground />
      <Route path="/about" component={AboutUsPage} transparentHeader whiteBackground />

      {/* Your events */}
      <Route path="/race-alerts" component={LoadableRaceAlerts} whiteBackground />
      <Route path="/saved-events" component={LoadableSavedEventsPage} />

      {/* Discovery pages */}
      <Route path="/:localeSlug/:sportSlug-events(/in-:locationSlug)" component={DiscoveryPage} transparentHeader />
      <Route path="/:localeSlug/:sportSlug-events(/:filterSlug)(/in-:locationSlug)" component={DiscoveryPage} transparentHeader />
      <Route path="/:sportSlug-events(/in-:locationSlug)" component={DiscoveryPage} transparentHeader onEnter={redirectDiscoveryWithLocationWithoutLocale} />
      <Route path="/:sportSlug-events(/:filterSlug)(/in-:locationSlug)" component={DiscoveryPage} transparentHeader onEnter={redirectDiscoveryWithLocationWithoutLocale} />

      {/* Mutations */}
      <Route path="/like-activity/:variables" component={LoadableLikeActivityPage} onEnter={authService.authenticateRoute} />
      <Route path="/unlike-activity/:variables" component={LoadableUnlikeActivityPage} onEnter={authService.authenticateRoute} />
      <Route path="/comment-activity/:variables" component={LoadableCommentOnActivityPage} onEnter={authService.authenticateRoute} />
      <Route path="/save-event/:eventId" component={LoadableSaveEventPage} onEnter={authService.authenticateRoute} />
      <Route path="/unsave-event/:eventId" component={LoadableUnsaveEventPage} onEnter={authService.authenticateRoute} />
      <Route path="/save-externalbooking/:eventId/:raceId" component={LoadableSaveExternalBookingPage} onEnter={authService.authenticateRoute} />
      <Route path="/unsave-externalbooking/:eventId/:racedId" component={LoadableUnsaveExternalBookingPage} onEnter={authService.authenticateRoute} />
      <Route path="/save-search/:variables" component={LoadableSaveSearchPage} onEnter={authService.authenticateRoute} />
      <Route path="/unsave-search/:id" component={LoadableUnsaveSearchPage} onEnter={authService.authenticateRoute} />

      { /* Search page */ }
      <Route path="s" component={LoadableEventSearchPage} noBanner noFooter />

      { /* Event pages */ }
      <Route path="/e/(:eventSlug-):eventId" component={EventDetailPage} whiteBackground />
      <Route path="/e/(:eventSlug-):eventId/review" component={EventReviewPage} onEnter={authService.authenticateRoute} />
      <Route path="/e/(:eventSlug-):eventId/tickets" component={EventTicketsPage} whiteBackground />
      <Redirect from="/e/(:eventSlug-):eventId/:raceId" to="/e/(:eventSlug-):eventId" status={301} />

      {/* User pages */}
      <Route path="/invite" onEnter={authService.authenticateRoute}>
        <Redirect from="facebook" to="/profile" />
        <Redirect from="strava" to="/profile" />
      </Route>

      <Route path="/profile/friends/invite" component={LoadablePrivateProfilePage} page="invite" onEnter={authService.authenticateRoute} whiteBackground />
      <Route path="/profile/friends" component={LoadablePrivateProfilePage} page="friends" onEnter={authService.authenticateRoute} whiteBackground />
      <Route path="/profile/interested" component={LoadablePrivateProfilePage} page="interested" onEnter={authService.authenticateRoute} />
      <Route path="/profile/past" component={LoadablePrivateProfilePage} page="past" onEnter={authService.authenticateRoute} />
      <Route path="/profile/upcoming" component={LoadablePrivateProfilePage} page="upcoming" onEnter={authService.authenticateRoute} />
      <Route path="/profile/reviews" component={LoadablePrivateProfilePage} page="reviews" onEnter={authService.authenticateRoute} />
      <Route path="/profile/settings" component={LoadableProfileSettingsPage} onEnter={authService.authenticateRoute} whiteBackground />
      <Route path="/profile" component={LoadablePrivateProfilePage} onEnter={authService.authenticateRoute} />

      <Route path="/profile/:profileId/friends" component={LoadablePublicProfilePage} page="friends" whiteBackground />
      <Route path="/profile/:profileId/interested" component={LoadablePublicProfilePage} page="interested" />
      <Route path="/profile/:profileId/past" component={LoadablePublicProfilePage} page="past" />
      <Route path="/profile/:profileId/upcoming" component={LoadablePublicProfilePage} page="upcoming" />
      <Route path="/profile/:profileId/reviews" component={LoadablePublicProfilePage} page="reviews" />
      <Route path="/profile/:profileId" component={LoadablePublicProfilePage} />

      <Route path="/profile/:profileId/friends/private" component={LoadablePrivateProfilePage} page="friends" onEnter={authService.adminRoute} whiteBackground />
      <Route path="/profile/:profileId/interested/private" component={LoadablePrivateProfilePage} page="interested" onEnter={authService.adminRoute} />
      <Route path="/profile/:profileId/past/private" component={LoadablePrivateProfilePage} page="past" onEnter={authService.adminRoute} />
      <Route path="/profile/:profileId/upcoming/private" component={LoadablePrivateProfilePage} page="upcoming" onEnter={authService.adminRoute} />
      <Route path="/profile/:profileId/reviews/private" component={LoadablePrivateProfilePage} page="reviews" onEnter={authService.adminRoute} />
      <Route path="/profile/:profileId/private" component={LoadablePrivateProfilePage} onEnter={authService.adminRoute} />

      { /* Booking page */ }
      <Route path="/book/:ticketId" component={LoadableBookingFormPage} noBanner noFooter />

      { /* Organiser page: */ }
      <Route path="/o/(:organiserSlug-):organiserId" component={OrganiserDetailPage} />
    </Route>

    <Route component={Page}>
      { /* Static pages */ }
      <Route path="privacy" component={LoadablePrivacy} />
      <Route path="terms" component={LoadableTerms} />

      { /* Catch all route */ }
      <Route path="*" component={LoadableNotFound} status={404} />
    </Route>
  </Route>
);

// Export routes to be shared by both client and server-side
module.exports = routes;



// WEBPACK FOOTER //
// ./app/src/routes/index.jsx